import React, { useState } from 'react';
import { X } from 'lucide-react';
import Input from './ui/Input';
import Select from './ui/Select';
import Button from './ui/Button';

const CallMeBackPopup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    countryCode: '+91',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const countryCodeOptions = [
    { value: '+91', label: '+91 (India)' },
    { value: '+1', label: '+1 (USA/Canada)' },
    { value: '+44', label: '+44 (UK)' },
    { value: '+61', label: '+61 (Australia)' },
    { value: '+971', label: '+971 (UAE)' },
    { value: '+65', label: '+65 (Singapore)' },
    { value: '+60', label: '+60 (Malaysia)' },
    { value: '+86', label: '+86 (China)' },
    { value: '+81', label: '+81 (Japan)' },
    { value: '+82', label: '+82 (South Korea)' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/?.test(formData?.phone?.trim())) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Prepare WhatsApp message
    const message = `Hello! I would like to request a callback.\n\nName: ${formData?.name}\nPhone: ${formData?.countryCode} ${formData?.phone}\nMessage: ${formData?.message}`;
    
    // WhatsApp number (replace with your actual WhatsApp number)
    const whatsappNumber = '917998666660'; // Replace with your WhatsApp number
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    // Redirect to WhatsApp
    window.open(whatsappUrl, '_blank');
    
    // Reset form and close popup
    setTimeout(() => {
      setIsSubmitting(false);
      setFormData({
        name: '',
        countryCode: '+91',
        phone: '',
        message: ''
      });
      onClose();
    }, 500);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">
              Call Me Back
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              aria-label="Close popup"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {submitSuccess ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Request Submitted!
              </h3>
              <p className="text-gray-600">
                We'll call you back shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Name"
                type="text"
                value={formData?.name}
                onChange={(e) => handleChange('name', e?.target?.value)}
                error={errors?.name}
                required
                placeholder="Enter your name"
              />

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Phone <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="w-32">
                    <Select
                      options={countryCodeOptions}
                      value={formData?.countryCode}
                      onChange={(value) => handleChange('countryCode', value)}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      type="tel"
                      value={formData?.phone}
                      onChange={(e) => handleChange('phone', e?.target?.value)}
                      error={errors?.phone}
                      required
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData?.message}
                  onChange={(e) => handleChange('message', e?.target?.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your message"
                />
                {errors?.message && (
                  <p className="text-sm text-red-500 mt-1">{errors?.message}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-3 rounded-lg transition-all duration-300"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallMeBackPopup;